import { Navigate, Route, Routes, useLocation } from 'react-router'

import { useSession } from '~/app/providers/SessionProvider'
import { PlainLayout } from '~/app/layouts/PlainLayout'
import { TabsLayout } from '~/app/layouts/TabsLayout'
import { useChannelGate } from '~/modules/access'
import { usePlatform } from '~/shared/platform'
import { IS_DEV, USE_MOCKS } from '~/shared/config'
import { LoadingState, Screen } from '~/shared/ui'

import { resolveRouteGuard } from './resolveRouteGuard'
import {
  BowlPage,
  ChannelGatePage,
  CompatHistoryPage,
  CompatResultPage,
  CompatStepTargetPage,
  CompatStepTypePage,
  HomePage,
  InboxPage,
  LinksCanvasPage,
  NatalDataPage,
  NatalIntroPage,
  NatalOtherResultPage,
  NatalResultPage,
  NotFoundPage,
  OnboardingPage,
  PersonPage,
  PrivacyPage,
  ProfilePage,
  ROUTE_PATHS,
  StatsPage,
  SubscriptionPage,
  SubscriptionThanksPage,
  WebStubPage,
} from './routes'

function SplashSkeleton() {
  return (
    <Screen>
      <LoadingState size='L' />
    </Screen>
  )
}

/**
 * Точка входа роутера. Редирект: онбординг не пройден → /onboarding,
 * пройден → /onboarding недоступен (docs/telegram.md §4).
 *
 * Поверх этого — гейт подписки на канал: пока бэк отвечает «доступа нет», единственный
 * доступный маршрут — `/gate`. Любой другой адрес (меню, «назад», прямая ссылка) уводит туда
 * же, а как только доступ появился — редирект снимается сам, потому что статус перепроверяется
 * фоном (modules/access).
 */
export function AppRouter() {
  const { status } = useSession()
  const platform = usePlatform()
  const location = useLocation()
  // Гейт спрашиваем только у вошедшего пользователя: ручка авторизованная, а новенькая
  // в онбординге и так внутри льготного периода.
  const { isBlocked } = useChannelGate({ isEnabled: status === 'ready' })

  // Открыли в обычном браузере, а не в Telegram: подписанных данных нет, войти нечем.
  //
  // Проверяем ДО статуса сессии, а не после: авторизация в этом случае не просто падает, а
  // может вообще не завершиться (запрос уходит в 401, клиент пробует переподписаться тем же
  // пустым initData), и человек остаётся в вечном скелетоне. Заодно не шлём заведомо
  // бессмысленный запрос.
  //
  // Только в собранном приложении: на дев-сервере браузер — рабочий режим разработки,
  // там вход подменяется моками или дев-байпасом.
  if (platform.kind === 'BROWSER' && !IS_DEV && !USE_MOCKS) {
    return <WebStubPage />
  }

  // Куда пускать — решает чистая `resolveRouteGuard` (она же покрыта тестами), компонент
  // только исполняет решение.
  const guard = resolveRouteGuard({ status, isBlocked, pathname: location.pathname })

  if (guard.kind === 'SPLASH') {
    return <SplashSkeleton />
  }

  if (guard.kind === 'REDIRECT') {
    return <Navigate to={guard.to} replace />
  }

  return (
    <Routes>
      <Route path={ROUTE_PATHS.onboarding} element={<OnboardingPage />} />

      {/* Вне TabsLayout: на экране гейта меню-острова не бывает. */}
      <Route path={ROUTE_PATHS.gate} element={<ChannelGatePage />} />

      <Route element={<TabsLayout />}>
        <Route path={ROUTE_PATHS.home} element={<HomePage />} />
        <Route path={ROUTE_PATHS.compat} element={<CompatStepTypePage />} />
        <Route path={ROUTE_PATHS.links} element={<LinksCanvasPage />} />
        <Route path={ROUTE_PATHS.stats} element={<StatsPage />} />
        <Route path={ROUTE_PATHS.profile} element={<ProfilePage />} />
      </Route>

      <Route element={<PlainLayout />}>
        <Route path={ROUTE_PATHS.compatTarget} element={<CompatStepTargetPage />} />
        <Route path={ROUTE_PATHS.compatResult} element={<CompatResultPage />} />
        <Route path={ROUTE_PATHS.linkPerson} element={<PersonPage />} />
        <Route path={ROUTE_PATHS.linksInbox} element={<InboxPage />} />
        <Route path={ROUTE_PATHS.profilePrivacy} element={<PrivacyPage />} />
        <Route path={ROUTE_PATHS.profileHistory} element={<CompatHistoryPage />} />
        <Route path={ROUTE_PATHS.natal} element={<NatalIntroPage />} />
        <Route path={ROUTE_PATHS.natalData} element={<NatalDataPage />} />
        <Route path={ROUTE_PATHS.natalResult} element={<NatalResultPage />} />
        {/* Разбор стал одной страницей с табами (правка основателя): дома и точки больше
            не живут на отдельных экранах. Старые адреса остаются редиректом — они могли
            попасть в историю браузера и в чужие сообщения. */}
        <Route
          path={ROUTE_PATHS.natalHouses}
          element={<Navigate to={ROUTE_PATHS.natalResult} replace />}
        />
        <Route
          path={ROUTE_PATHS.natalDeep}
          element={<Navigate to={ROUTE_PATHS.natalResult} replace />}
        />
        {/* Ввод данных чужой карты переехал на общий `/natal/data` (правка основателя №8) —
            старый адрес оставлен редиректом ради ссылок, которые уже могли уйти наружу. */}
        <Route
          path={ROUTE_PATHS.natalOther}
          element={<Navigate to={ROUTE_PATHS.natalData} replace />}
        />
        <Route path={ROUTE_PATHS.natalOtherResult} element={<NatalOtherResultPage />} />
        <Route path={ROUTE_PATHS.bowl} element={<BowlPage />} />
        <Route path={ROUTE_PATHS.subscription} element={<SubscriptionPage />} />
        <Route path={ROUTE_PATHS.subscriptionThanks} element={<SubscriptionThanksPage />} />
      </Route>

      <Route path='*' element={<NotFoundPage />} />
    </Routes>
  )
}
